"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import type { NavLink } from "@/constants/navbarMenu";
import { ChevronDownIcon, ChevronRightIcon } from "@/assets/icons";
import { Aside, Button, Span } from "@/components/ui";
import AppLink from "@/components/i18n/AppLink";
import { usePopoverDismiss } from "@/hooks/usePopoverDismiss";
import { useNavPathLabel } from "@/hooks/useNavPathLabel";

const FLAT_TAB_CHUNK = 8;

function resolvedNavRootLabel(link: NavLink, tNav: (key: string) => string): string {
  if (link.i18nKey) return tNav(link.i18nKey);
  return link.label ?? "";
}

function flatTabsFromChildren(
  children: NavLink[],
  browseFallback: string,
): { label: string; items: NavLink[] }[] {
  if (!children?.length) return [];
  if (children.length <= FLAT_TAB_CHUNK) {
    return [{ label: browseFallback, items: children }];
  }
  const tabs: { label: string; items: NavLink[] }[] = [];
  for (let i = 0; i < children.length; i += FLAT_TAB_CHUNK) {
    const items = children.slice(i, i + FLAT_TAB_CHUNK);
    const first = items[0]?.label?.trim() ?? "";
    const last = items[items.length - 1]?.label?.trim() ?? "";
    const label = first && last && first !== last ? `${first} – ${last}` : first || last || browseFallback;
    tabs.push({ label, items });
  }
  return tabs;
}

function MegaNestLinks({
  section,
  getSecondLabel,
}: {
  section: NavLink;
  getSecondLabel: (n: NavLink) => string;
}) {
  const chunkedChildren = section.children?.reduce(
    (rows: NavLink[][], child, index) => {
      const chunkIndex = Math.floor(index / 10);
      if (!rows[chunkIndex]) rows[chunkIndex] = [];
      rows[chunkIndex].push(child);
      return rows;
    },
    [],
  );

  return (
    <div className="flex flex-wrap gap-x-10 gap-y-1">
      {chunkedChildren?.map((group, colIndex) => (
        <div key={colIndex} className="navbar-mega-link-stack min-w-[10rem]">
          {group.map((second) => (
            <AppLink
              key={`${second.id}-${second.href ?? ""}`}
              href={second.href ?? "#"}
              className="navbar-mega-link"
              title={getSecondLabel(second)}
            >
              {getSecondLabel(second)}
            </AppLink>
          ))}
        </div>
      ))}
    </div>
  );
}

type NavItemProps = {
  link: NavLink;
};

const NavItem: React.FC<NavItemProps> = ({ link }) => {
  const tNav = useTranslations("nav");
  const tNavStr = (key: string) => tNav(key as Parameters<typeof tNav>[0]);
  const resolveNavLabel = useNavPathLabel();

  const desktopRootRef = useRef<HTMLDivElement>(null);
  const desktopTriggerRef = useRef<HTMLButtonElement>(null);

  const [megaTabIndex, setMegaTabIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [mobileDrawer, setMobileDrawer] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const closeMega = useCallback(() => {
    setIsOpen(false);
    desktopTriggerRef.current?.focus();
  }, []);

  usePopoverDismiss({ open: isOpen, onClose: closeMega, rootRef: desktopRootRef });

  const rootLabel = resolvedNavRootLabel(link, tNavStr);

  const hasChildren = Boolean(link.children && link.children.length > 0);
  const hasNested = Boolean(link.children?.some((c) => c.children && c.children.length > 0));

  const flatTabs = useMemo(() => {
    if (!link.children || hasNested) return [];
    const browse = tNav("ui.browse" as Parameters<typeof tNav>[0]);
    return flatTabsFromChildren(link.children, browse).map((tab) => ({
      ...tab,
      items: tab.items,
      label:
        tab.items.length === link.children!.length
          ? browse
          : `${resolveNavLabel(tab.items[0]!)} – ${resolveNavLabel(tab.items[tab.items.length - 1]!)}`,
    }));
  }, [link.children, hasNested, tNav, resolveNavLabel]);

  useEffect(() => {
    setMegaTabIndex(0);
  }, [link.id]);

  useEffect(() => {
    if (isOpen) setMegaTabIndex(0);
  }, [isOpen]);

  useEffect(() => {
    if (!mobileDrawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setMobileDrawer(false);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [mobileDrawer]);

  const secondLabel = (n: NavLink) => resolveNavLabel(n);

  if (!hasChildren) {
    return (
      <AppLink
        href={link.href ?? "#"}
        external={link.external}
        className={clsx("navbar-nav-link", link.disabled && "pointer-events-none opacity-50")}
        title={rootLabel}
      >
        {rootLabel}
      </AppLink>
    );
  }

  return (
    <>
      <div
        ref={desktopRootRef}
        className="relative hidden bg-transparent md:inline-block"
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <Button
          ref={desktopTriggerRef}
          type="button"
          className="navbar-nav-trigger"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          disabled={link.disabled}
          onClick={() => setIsOpen(!isOpen)}
        >
          <Span>{rootLabel}</Span>
          <ChevronDownIcon
            width={10}
            height={10}
            strokeWidth={2}
            className={clsx("navbar-nav-chevron text-current", isOpen && "rotate-180")}
          />
        </Button>

        {isOpen && (
          <div className="navbar-mega-dropdown animate__animated animate__fadeInDownNavDropdown">
            <div className="navbar-mega-inner">
              {hasNested ? (
                <>
                  <div className="navbar-mega-tabs" role="tablist" aria-label={rootLabel}>
                    {link.children!.map((child, i) => (
                      <button
                        key={child.label ?? child.id}
                        type="button"
                        role="tab"
                        aria-selected={megaTabIndex === i}
                        tabIndex={megaTabIndex === i ? 0 : -1}
                        className={clsx("navbar-mega-tab", megaTabIndex === i && "is-active")}
                        onMouseEnter={() => setMegaTabIndex(i)}
                        onFocus={() => setMegaTabIndex(i)}
                        onClick={() => setMegaTabIndex(i)}
                      >
                        {secondLabel(child)}
                      </button>
                    ))}
                  </div>
                  <div className="navbar-mega-tab-panel" role="tabpanel">
                    <MegaNestLinks section={link.children![megaTabIndex]} getSecondLabel={secondLabel} />
                  </div>
                </>
              ) : (
                <>
                  {flatTabs.length > 1 ? (
                    <div className="navbar-mega-tabs" role="tablist" aria-label={rootLabel}>
                      {flatTabs.map((tab, i) => (
                        <button
                          key={tab.label}
                          type="button"
                          role="tab"
                          aria-selected={megaTabIndex === i}
                          tabIndex={megaTabIndex === i ? 0 : -1}
                          className={clsx("navbar-mega-tab", megaTabIndex === i && "is-active")}
                          onMouseEnter={() => setMegaTabIndex(i)}
                          onFocus={() => setMegaTabIndex(i)}
                          onClick={() => setMegaTabIndex(i)}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <nav className="navbar-mega-columns" aria-label={rootLabel}>
                    {(flatTabs[megaTabIndex]?.items ?? []).map((child) => (
                      <AppLink
                        key={child.label ?? child.id}
                        href={child.href ?? "#"}
                        className="navbar-mega-link"
                        title={secondLabel(child)}
                      >
                        {secondLabel(child)}
                      </AppLink>
                    ))}
                  </nav>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="block md:hidden">
        <Button
          type="button"
          aria-expanded={mobileDrawer}
          onClick={() => setMobileDrawer(true)}
          className="navbar-mobile-sheet-trigger"
        >
          <Span>{rootLabel}</Span>
          <ChevronDownIcon
            width={10}
            height={10}
            strokeWidth={2}
            className={clsx("navbar-nav-chevron", mobileDrawer && "rotate-180")}
            aria-hidden
          />
        </Button>

        {mobileDrawer && (
          <div className="navbar-nested-menu-modal" role="dialog" aria-modal="true" aria-label={rootLabel}>
            <button
              type="button"
              className="navbar-nested-menu-modal__backdrop"
              aria-label="Close menu"
              onClick={() => setMobileDrawer(false)}
            />
            <Aside className="navbar-nested-menu-modal__panel">
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <div className="flex items-center justify-between border-b border-[color-mix(in_srgb,var(--semantic-border-default)_45%,transparent)] p-4">
                <Span>{rootLabel}</Span>
                <Button type="button" onClick={() => setMobileDrawer(false)}>
                  ✕
                </Button>
              </div>

              <div className="space-y-3 p-4">
                {link.children?.map((child) => {
                  const hasGrandChildren = Boolean(child.children && child.children.length > 0);
                  const childKey = String(child.label ?? child.id);
                  const isExpanded = expanded === childKey;

                  return (
                    <div key={childKey}>
                      <div
                        className={clsx("navbar-mobile-row", isExpanded && "is-expanded")}
                        onClick={() =>
                          hasGrandChildren ? setExpanded(isExpanded ? null : childKey) : setMobileDrawer(false)
                        }
                      >
                        <AppLink
                          href={child.href ?? "#"}
                          className="navbar-mega-link flex-1 font-normal"
                          title={secondLabel(child)}
                          onClick={(e) => {
                            if (hasGrandChildren) e.preventDefault();
                          }}
                        >
                          {secondLabel(child)}
                        </AppLink>

                        {hasGrandChildren ? (
                          <ChevronRightIcon
                            width={16}
                            height={16}
                            strokeWidth={2}
                            className={clsx("navbar-mobile-chevron", isExpanded && "rotate-90")}
                            aria-hidden
                          />
                        ) : null}
                      </div>

                      {hasGrandChildren && isExpanded && (
                        <div className="mt-1 space-y-2 ps-6">
                          {child.children?.map((grand) => (
                            <AppLink
                              key={grand.label ?? grand.id}
                              href={grand.href ?? "#"}
                              className="navbar-mega-link block"
                              title={resolveNavLabel(grand)}
                              onClick={() => setMobileDrawer(false)}
                            >
                              {resolveNavLabel(grand)}
                            </AppLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
            </Aside>
          </div>
        )}
      </div>
    </>
  );
};

export default NavItem;
